import type { NotificationListItem } from './apiContracts';
import type { PrototypeState } from './prototypeHydration';
import { createNotificationIcon } from './notificationVisual';
import { topicReadyPathFromNotification } from './notificationActionRoute';
import { renderEmptyStateMarkup } from './emptyStateMarkup';

const POLL_INTERVAL_MS = 300_000;
const PANEL_LIMIT = 3;
const PASSIVE_REFRESH_FRESHNESS_MS = 30_000;
const mountedControllers = new WeakMap<HTMLElement, NotificationCenterController>();

interface Scheduler {
	setInterval(callback: () => void, delayMs: number): ReturnType<typeof setInterval>;
	clearInterval(handle: ReturnType<typeof setInterval>): void;
}

interface NotificationCenterOptions {
	deferInitialAuth?: boolean;
}

export interface NotificationCenterController {
	readonly pollIntervalMs: number;
	syncAuth(): Promise<void>;
	refresh(): Promise<void>;
	destroy(): void;
}

function setText(element: HTMLElement, value: string): void {
	element.textContent = value;
}

function timeLabel(value: string): string {
	const date = new Date(value);
	return Number.isFinite(date.getTime()) ? new Intl.DateTimeFormat('en', { month: 'short', day: 'numeric' }).format(date) : '';
}

export function createNotificationCenterController(
	root: HTMLElement,
	state: PrototypeState,
	scheduler: Scheduler = { setInterval: globalThis.setInterval.bind(globalThis), clearInterval: globalThis.clearInterval.bind(globalThis) },
	options: NotificationCenterOptions = {},
): NotificationCenterController {
	const doc = root.ownerDocument;
	const win = doc.defaultView;
	const center = root.querySelector<HTMLElement>('#notificationCenter');
	const bell = root.querySelector<HTMLButtonElement>('#notificationBell');
	const badge = root.querySelector<HTMLElement>('#notificationBadge');
	const panel = root.querySelector<HTMLElement>('#notificationPanel');
	const list = root.querySelector<HTMLElement>('#notificationList');
	const empty = root.querySelector<HTMLElement>('#notificationEmpty');
	const skeleton = root.querySelector<HTMLElement>('#notificationSkeleton');
	const error = root.querySelector<HTMLElement>('#notificationError');
	const more = root.querySelector<HTMLButtonElement>('#notificationMore');
	let items: NotificationListItem[] = [];
	let unreadCount = 0;
	let expandedId: string | null = null;
	let pollHandle: ReturnType<typeof setInterval> | null = null;
	let inFlight: { generation: number; promise: Promise<void> } | null = null;
	let authKey = '';
	let generation = 0;
	let destroyed = false;
	let authResolved = false;
	let lastSuccessfulRefreshAt = 0;
	if (options.deferInitialAuth && state.isLoggedIn && center) {
		delete center.dataset.authPending;
		center.style.display = 'flex';
	}
	paintPanelChrome();

	// The markup ships slot-only (no visible words) so raw HTML carries no
	// notification chrome text for non-JS crawlers; the panel is only ever
	// opened through JS, so painting the chrome here is always in time.
	function paintPanelChrome(): void {
		const title = root.querySelector<HTMLElement>('#notificationTitle');
		if (title) title.textContent = 'Notifications';
		if (more) more.textContent = 'More';
	}

	function paintEmptyState(): void {
		if (!empty) return;
		if (empty.childElementCount > 0) return;
		empty.innerHTML = renderEmptyStateMarkup({
			icon: 'inbox',
			title: 'You are all caught up.',
			subtitle: 'New notifications will appear here.',
		});
	}

	function renderBadge(): void {
		if (!badge) return;
		badge.hidden = unreadCount <= 0;
		setText(badge, unreadCount > 99 ? '99+' : String(unreadCount));
	}

	function renderList(): void {
		if (!list || !empty) return;
		list.replaceChildren();
		if (error) error.hidden = true;
		const visible = items.slice(0, PANEL_LIMIT);
		paintEmptyState();
		empty.hidden = visible.length > 0;
		for (const item of visible) {
			const article = doc.createElement('article');
			article.className = `ntf-item${item.readAt ? '' : ' unread'}${expandedId === item.id ? ' expanded' : ''}`;
			const button = doc.createElement('button');
			button.type = 'button';
			button.className = 'ntf-item-button';
			button.dataset.notificationId = item.id;
			button.setAttribute('aria-expanded', String(expandedId === item.id));
			const icon = doc.createElement('span');
			icon.className = 'ntf-item-icon';
			icon.appendChild(createNotificationIcon(doc, item.category, 'ntf-category-svg'));
			const title = doc.createElement('span');
			title.className = 'ntf-item-title';
			setText(title, item.title);
			const when = doc.createElement('time');
			when.className = 'ntf-item-time';
			when.dateTime = item.createdAt;
			setText(when, timeLabel(item.createdAt));
			button.appendChild(icon);
			button.appendChild(title);
			button.appendChild(when);
			article.appendChild(button);
			if (expandedId === item.id) {
				const body = doc.createElement('p');
				body.className = 'ntf-item-body';
				setText(body, item.body);
				article.appendChild(body);
			}
			list.appendChild(article);
		}
	}

	function renderSkeleton(): void {
		if (!list || !empty || !skeleton) return;
		list.replaceChildren();
		skeleton.replaceChildren();
		if (error) error.hidden = true;
		empty.hidden = true;
		skeleton.hidden = false;
		for (let i = 0; i < PANEL_LIMIT; i++) {
			const row = doc.createElement('div');
			row.className = 'ntf-skeleton-row';
			const line1 = doc.createElement('div');
			line1.className = 'ntf-skeleton-bar ntf-skeleton-bar--w60';
			const line2 = doc.createElement('div');
			line2.className = 'ntf-skeleton-bar ntf-skeleton-bar--w30';
			row.appendChild(line1);
			row.appendChild(line2);
			skeleton.appendChild(row);
		}
	}

	function clearState(): void {
		items = [];
		unreadCount = 0;
		expandedId = null;
		hideSkeleton();
		list?.replaceChildren();
		if (empty) empty.hidden = true;
		if (error) error.hidden = true;
		renderBadge();
	}

	function currentAuthKey(): string {
		return state.isLoggedIn && state.runtime.userId ? `user:${state.runtime.userId}` : 'visitor';
	}

	function renderLoadError(): void {
		hideSkeleton();
		if (!error) return;
		if (error.childElementCount === 0) {
			// The markup ships this slot empty (slot-only contract); the retry
			// button must exist before the slot is unhidden or the click below
			// (#notificationRetry) has nothing to land on.
			const message = doc.createElement('span');
			setText(message, 'Notifications could not be loaded.');
			const retry = doc.createElement('button');
			retry.type = 'button';
			retry.id = 'notificationRetry';
			setText(retry, 'Retry');
			error.appendChild(message);
			error.appendChild(retry);
		}
		if (empty) empty.hidden = true;
		error.hidden = false;
	}

	function hideSkeleton(): void {
		if (skeleton) {
			skeleton.hidden = true;
			skeleton.replaceChildren();
		}
	}

	async function refreshWithPolicy(force: boolean): Promise<void> {
		if (destroyed || !state.isLoggedIn || !state.runtime.userId) return;
		const capturedGeneration = generation;
		const capturedKey = authKey;
		if (inFlight?.generation === capturedGeneration) return inFlight.promise;
		if (!force && lastSuccessfulRefreshAt > 0 && Date.now() - lastSuccessfulRefreshAt < PASSIVE_REFRESH_FRESHNESS_MS) return;
		const promise = (async () => {
			const [countResult, listResult] = await Promise.allSettled([
				state.runtime.api.getUnreadNotificationCount(),
				state.runtime.api.listNotifications({ limit: PANEL_LIMIT }),
			]);
			if (destroyed || capturedGeneration !== generation || capturedKey !== authKey) return;
			if (countResult.status === 'fulfilled') unreadCount = Math.max(0, countResult.value.unreadCount);
			renderBadge();
			if (listResult.status === 'fulfilled') {
				items = listResult.value.items;
				hideSkeleton();
				renderList();
			} else {
				renderLoadError();
			}
			if (countResult.status === 'fulfilled' && listResult.status === 'fulfilled') lastSuccessfulRefreshAt = Date.now();
		})().finally(() => {
			if (inFlight?.generation === capturedGeneration) inFlight = null;
		});
		inFlight = { generation: capturedGeneration, promise };
		return promise;
	}

	function refresh(): Promise<void> {
		return refreshWithPolicy(true);
	}

	function stopPolling(): void {
		if (pollHandle === null) return;
		scheduler.clearInterval(pollHandle);
		pollHandle = null;
	}

	async function syncAuth(): Promise<void> {
		if (!center) return;
		if (options.deferInitialAuth && !authResolved) return;
		const nextAuthKey = currentAuthKey();
		const identityChanged = nextAuthKey !== authKey;
		if (identityChanged) {
			authKey = nextAuthKey;
			generation += 1;
			inFlight = null;
			lastSuccessfulRefreshAt = 0;
			clearState();
		}
		if (nextAuthKey === 'visitor') {
			stopPolling();
			if (!authResolved) return;
			delete center.dataset.authPending;
			center.style.display = 'none';
			if (panel) panel.hidden = true;
			if (bell) bell.setAttribute('aria-expanded', 'false');
			return;
		}
		delete center.dataset.authPending;
		center.style.display = 'flex';
		if (pollHandle === null) {
			pollHandle = scheduler.setInterval(() => {
				if (doc.visibilityState === 'visible') void refreshWithPolicy(false);
			}, POLL_INTERVAL_MS);
		}
		await refreshWithPolicy(identityChanged);
	}

	async function readOne(id: string): Promise<void> {
		const capturedGeneration = generation;
		const capturedKey = authKey;
		expandedId = expandedId === id ? null : id;
		const item = items.find((candidate) => candidate.id === id);
		renderList();
		if (item && !item.readAt && expandedId === id) {
			try {
				const result = await state.runtime.api.markNotificationRead(id);
				if (destroyed || capturedGeneration !== generation || capturedKey !== authKey) return;
				item.readAt = result.readAt;
				unreadCount = Math.max(0, unreadCount - 1);
			} catch {
				// Preserve the message and retry on the next explicit interaction.
			}
		}
		renderBadge();
		renderList();
	}

	function openTopicReady(item: NotificationListItem, path: string): void {
		if (!item.readAt) void state.runtime.api.markNotificationRead(item.id).catch(() => undefined);
		// Start the read mutation as best effort, but never wait for it before navigation.
		window.history.pushState({}, '', path);
		window.dispatchEvent(new PopStateEvent('popstate'));
	}

	const onClick = (event: MouseEvent) => {
		const target = event.target as HTMLElement | null;
		if (target?.closest('#notificationBell')) {
			if (!panel || !bell) return;
			const willOpen = panel.hidden;
			panel.hidden = !panel.hidden;
			bell.setAttribute('aria-expanded', String(!panel.hidden));
			if (willOpen) {
				// Instant render of cached items so the panel is never blank.
				// If items exist from a previous fetch, they paint immediately
				// and refresh() updates them in the background. A previous
				// successful fetch with zero items is also a known state —
				// paint the empty panel instead of skeleton rows. Otherwise a
				// passive refresh skipped by the freshness window (≤30s) would
				// leave a zero-notification account stuck on the skeleton.
				// Only show skeleton rows when we have never fetched at all.
				if (lastSuccessfulRefreshAt > 0) {
					renderList();
				} else {
					renderSkeleton();
				}
				void refreshWithPolicy(false);
			}
			return;
		}
		if (target?.closest('#notificationMore')) {
			// "More" link navigates to /messages via showView; close panel.
			if (panel) panel.hidden = true;
			if (bell) bell.setAttribute('aria-expanded', 'false');
			return;
		}
		if (target?.closest('#notificationRetry')) {
			void refresh();
			return;
		}
		// Message-page rows use the same data attribute. Scope this delegated handler
		// to the bell panel so one click cannot issue a duplicate read request.
		const itemButton = target?.closest<HTMLButtonElement>('#notificationPanel [data-notification-id]');
		if (itemButton?.dataset.notificationId) {
			const item = items.find((candidate) => candidate.id === itemButton.dataset.notificationId);
			const topicPath = item && topicReadyPathFromNotification(item.eventType, item.actionUrl);
			if (item && topicPath) openTopicReady(item, topicPath);
			else void readOne(itemButton.dataset.notificationId);
		}
		else if (panel && !panel.hidden && !target?.closest('#notificationCenter')) {
			panel.hidden = true;
			bell?.setAttribute('aria-expanded', 'false');
		}
	};
	const onFocus = () => { if (state.isLoggedIn && doc.visibilityState === 'visible') void refreshWithPolicy(false); };
	const onVisibility = () => { if (state.isLoggedIn && doc.visibilityState === 'visible') void refreshWithPolicy(false); };
	const onAuthChange = () => {
		authResolved = true;
		void syncAuth();
	};
	const onNotificationInvalidated = () => {
		if (!state.isLoggedIn) return;
		generation += 1;
		inFlight = null;
		lastSuccessfulRefreshAt = 0;
		items = [];
		expandedId = null;
		if (panel && !panel.hidden) renderSkeleton();
		void refreshWithPolicy(true);
	};
	doc.addEventListener('click', onClick);
	const onTouchStart = (event: TouchEvent) => {
		if (!panel || panel.hidden) return;
		const target = event.target as HTMLElement | null;
		if (!target?.closest('#notificationCenter')) {
			panel.hidden = true;
			bell?.setAttribute('aria-expanded', 'false');
		}
	};
	doc.addEventListener('touchstart', onTouchStart, { passive: true });
	doc.addEventListener('visibilitychange', onVisibility);
	doc.addEventListener('deepmox:auth-state-changed', onAuthChange);
	doc.addEventListener('deepmox:notifications-invalidated', onNotificationInvalidated);
	win?.addEventListener('focus', onFocus);

	// Note: #notificationMore relies on the body-level delegated handler in
	// prototypeEventBindings.ts to process its data-proto-click attribute.
	// We must NOT call stopPropagation() here — that would block the event
	// from reaching document.body, preventing showView('messages') from firing.

	const controller: NotificationCenterController = {
		pollIntervalMs: POLL_INTERVAL_MS,
		syncAuth, refresh,
		destroy() {
			destroyed = true;
			stopPolling();
			doc.removeEventListener('click', onClick);
			doc.removeEventListener('touchstart', onTouchStart);
			doc.removeEventListener('visibilitychange', onVisibility);
			doc.removeEventListener('deepmox:auth-state-changed', onAuthChange);
			doc.removeEventListener('deepmox:notifications-invalidated', onNotificationInvalidated);
			win?.removeEventListener('focus', onFocus);
			mountedControllers.delete(root);
		},
	};
	mountedControllers.set(root, controller);
	void syncAuth();
	return controller;
}
